import React, { createContext, useReducer, useContext } from 'react';
import initialState from './initialState';
import { buildLinkInfoModel } from '../model/linkInformationModel';
import { buildManifestModel } from '../model/manifestModel';
import { resetSessionTimer } from '../commonUtil/sessionUtil';
import { getContentVersionId } from '../commonUtil/getContentVersionId';
import { getContentVersionIdByManifest } from '../commonUtil/getContentVersionIdByManifest';

export const ACTION_TYPES_LOAD_DATA = 'ACTION_TYPES_LOAD_DATA';
export const ACTION_TYPES_LOAD_MANIFEST_DATA = 'ACTION_TYPES_LOAD_MANIFEST_DATA';
export const ACTION_TYPES_LOADING_MANIFEST_DATA = 'ACTION_TYPES_LOADING_MANIFEST_DATA';
export const ACTION_TYPES_CHANGE_CONTENT = 'ACTION_TYPES_CHANGE_CONTENT';
export const ACTION_TYPES_SERVICE_ERROR = 'ACTION_TYPES_SERVICE_ERROR';
export const ACTION_TYPES_UPDATE_SENDER_INFO = 'ACTION_TYPES_UPDATE_SENDER_INFO';
export const ACTION_TYPES_DETECT_VIEWER_LOCATION = 'ACTION_TYPES_DETECT_VIEWER_LOCATION';
export const ACTION_TYPES_LOAD_MICROSOFT_SCRIPT = 'ACTION_TYPES_LOAD_MICROSOFT_SCRIPT';
export const ACTION_TYPES_SHOW_CUSTOM_FOOTER = 'ACTION_TYPES_SHOW_CUSTOM_FOOTER';
export const ACTION_TYPES_SHOW_SHARE_MODAL = 'ACTION_TYPES_SHOW_SHARE_MODAL';
export const ACTION_TYPES_CREATING_SESSION = 'ACTION_TYPES_CREATING_SESSION';
export const ACTION_TYPES_IS_SKIP_SPLASH_SCREEN = 'ACTION_TYPES_IS_SKIP_SPLASH_SCREEN';
export const ACTION_TYPES_UNIVERSAL_PLAYER_EVENT = 'ACTION_TYPES_UNIVERSAL_PLAYER_EVENT';
export const ACTION_TYPES_IS_ONLY_FUNCTIONAL_COOKIES = 'ACTION_TYPES_IS_ONLY_FUNCTIONAL_COOKIES';
export const ACTION_TYPES_UPDATE_DEFAULT_PRIVACY_URL = 'ACTION_TYPES_UPDATE_DEFAULT_PRIVACY_URL';
export const ACTION_TYPES_UPDATE_CONTENT_ASSETS = 'ACTION_TYPES_UPDATE_CONTENT_ASSETS';
export const ACTION_TYPES_IS_STRICTLY_NECESSARY_COOKIES = 'ACTION_TYPES_IS_STRICTLY_NECESSARY_COOKIES';
export const ACTION_TYPES_VALIDATE_PASSWORD = 'ACTION_TYPES_VALIDATE_PASSWORD';
export const ACTION_TYPES_IS_DISABLED_GDPR = 'ACTION_TYPES_IS_DISABLED_GDPR';
export const ACTION_TYPES_IS_LOAD_MANIFEST_DIRECT = 'ACTION_TYPES_IS_LOAD_MANIFEST_DIRECT';
export const ACTION_TYPES_REMEMBER_PASSWORD = 'ACTION_TYPES_REMEMBER_PASSWORD';
export const ACTION_TYPES_ENABLED_WATERMARK = 'ACTION_TYPES_ENABLED_WATERMARK';

const reducer = (state, action) => {
    const { type, payload } = action;
    switch (type) {
    case ACTION_TYPES_LOAD_DATA: {
        const linkInfoModel = buildLinkInfoModel(payload, state.isWatermarkEnabled);

        return { ...state,
            ...linkInfoModel,
            isLoadingLink: false
        };
    }

    case ACTION_TYPES_LOAD_MANIFEST_DATA: {
        const manifestModel = buildManifestModel(payload, state);
        return { ...state, ...manifestModel, isLoadingManifest: false };
    }

    case ACTION_TYPES_LOADING_MANIFEST_DATA: {
        return { ...state, isLoadingManifest: payload };
    }

    case ACTION_TYPES_IS_SKIP_SPLASH_SCREEN: {
        return { ...state, skipSplashScreen: payload };
    }

    case ACTION_TYPES_IS_ONLY_FUNCTIONAL_COOKIES: {
        return { ...state, isOnlyFunctionalCookies: payload };
    }

    case ACTION_TYPES_CHANGE_CONTENT: {
        const { contents, manifests } = state;

        const selectedContentId = payload.selectedContentId;
        const selectedContent = { ...contents.find(ct => ct.id === selectedContentId) };
        const selectedContentVersionId = getContentVersionId(selectedContent);
        const selectedManifest = manifests.length > 0 ? { ...manifests.find(mf => mf.Id === selectedContentId || getContentVersionIdByManifest(mf?.OriginalManifests) === selectedContentVersionId)?.OriginalManifests } : {};
        return { ...state, selectedContent, selectedManifest };
    }

    case ACTION_TYPES_SERVICE_ERROR: {
        console.error(payload);
        return { ...state, error: payload };
    }

    case ACTION_TYPES_UPDATE_SENDER_INFO: {
        return { ...state, sender: payload };
    }

    case ACTION_TYPES_DETECT_VIEWER_LOCATION: {
        return { ...state, viewerLocation: payload };
    }

    case ACTION_TYPES_UNIVERSAL_PLAYER_EVENT: {
        return { ...state, universalPlayerEvent: payload };
    }

    case ACTION_TYPES_LOAD_MICROSOFT_SCRIPT: {
        return { ...state, isLoadingScript: payload };
    }

    case ACTION_TYPES_SHOW_CUSTOM_FOOTER: {
        return { ...state, hasCustomFooter: payload };
    }

    case ACTION_TYPES_SHOW_SHARE_MODAL: {
        resetSessionTimer();
        return { ...state, showShareModal: payload };
    }

    case ACTION_TYPES_CREATING_SESSION: {
        return { ...state, creatingSession: payload };
    }

    case ACTION_TYPES_UPDATE_DEFAULT_PRIVACY_URL: {
        return { ...state, gdprSetting: { ...state.gdprSetting, privacyLink: payload } };
    }

    case ACTION_TYPES_UPDATE_CONTENT_ASSETS: {
        return { ...state, downloadAssets: payload };
    }

    case ACTION_TYPES_IS_STRICTLY_NECESSARY_COOKIES : {
        return { ...state, isStrictlyNecessaryCookies: payload };
    }

    case ACTION_TYPES_VALIDATE_PASSWORD: {
        return { ...state, ...payload };
    }

    case ACTION_TYPES_IS_DISABLED_GDPR: {
        return { ...state, isDisabledGDPR: payload };
    }

    case ACTION_TYPES_IS_LOAD_MANIFEST_DIRECT: {
        return { ...state, isLoadManifestDirect: payload };
    }

    case ACTION_TYPES_REMEMBER_PASSWORD: {
        return { ...state, passwords: payload };
    }

    case ACTION_TYPES_ENABLED_WATERMARK: {
        return { ...state, isWatermarkEnabled: payload };
    }

    default:
        return state;
    }
};

export const LinkContext = createContext({});
export const LinkContextWrapper = (props) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    return (
        <LinkContext.Provider value={{ state, dispatch }}>
            {props.children}
        </LinkContext.Provider>
    );
};

export const withLinkStore = WrappedComponent => (props) => {
    const { state, dispatch } = useContext(LinkContext);
    return (<WrappedComponent
        {...props}
        linkStore={state}
        linkDispatch={dispatch}
    />);
};

